import {
  BasicPlaybook,
  PlaybookLandingPageTypes,
  PlaybookReportType,
  User,
} from '@driftt/api-types'
import {
  Box,
  Flex,
  Icon,
  Select,
  StatusBadge,
  Table,
  Text,
  Tooltip,
  useTableState,
} from '@driftt/dds'
import { faCircleInfo } from '@driftt/dds.icons/light/faCircleInfo'
import * as R from 'ramda'
import React from 'react'
import { useEffect, useState } from 'react'

import { Fallback } from 'components/PlaybooksTable/components'
import {
  CLP_AS_PLAYBOOK_RELEASE_TIME,
  CLP_PLAYBOOK_TYPE,
  LANDING_PAGE_PLAYBOOK_REPORT_TYPES,
  REPORT_TYPE_TO_TABLE_NAME,
} from 'constants/types'
import { formatTime } from 'utils/moment'

interface PriorityPayload {
  idPriorityList: number[]
  playbookTypes: {
    outbound: BasicPlaybook[]
    ABM: BasicPlaybook[]
    nonSpecial: BasicPlaybook[]
  }
}

interface ChangePriority {
  (option: { label: string; value: number }, playbook: BasicPlaybook): void
}

interface Props {
  priorityPayload: PriorityPayload
  changePriority: ChangePriority
  orgUsers: Record<number, User>
  isLoading: boolean
}

const generateSelectOptions = ({
  offset = 0,
  max,
}: {
  offset?: number
  max: number
}) => {
  const list = []
  for (let i = 0; i < max; i++) {
    const opt = i + offset
    list[i] = { label: String(opt), value: opt }
  }
  return list
}

const PrioritizeTable = ({
  priorityPayload,
  changePriority,
  orgUsers,
  isLoading,
}: Props) => {
  const offsets = {
    outbound: 0,
    ABM: priorityPayload.playbookTypes.outbound.length,
    nonSpecial:
      priorityPayload.playbookTypes.outbound.length +
      priorityPayload.playbookTypes.ABM.length,
  }
  const [prioritizedPlaybooks, setPrioritizedPlaybooks] = useState<
    BasicPlaybook[]
  >([])

  const { tableState } = useTableState({
    defaultPagination: {
      pageIndex: 0,
      pageSize: 25,
    },
  })

  const [total, setTotal] = useState<number>(tableState.pagination.pageSize)

  useEffect(() => {
    if (R.isEmpty(priorityPayload)) return setPrioritizedPlaybooks([])

    const combinedPlaybooks = [
      ...priorityPayload.playbookTypes.outbound,
      ...priorityPayload.playbookTypes.ABM,
      ...priorityPayload.playbookTypes.nonSpecial,
    ]

    const playbooksById = R.indexBy(R.prop('id'), combinedPlaybooks)

    const sortedPlaybooks = priorityPayload.idPriorityList
      .map((id, index) => {
        const playbook = playbooksById[id]
        return playbook ? { ...playbook, priority: index + 1 } : null
      })
      .filter(Boolean) as BasicPlaybook[]

    setPrioritizedPlaybooks(sortedPlaybooks)
    setTotal(sortedPlaybooks.length)
  }, [priorityPayload])

  const isOutboundType = (type: string) =>
    type === PlaybookReportType.TALK_TO_YOUR_OUTBOUND_PROSPECTS

  const isABMType = (type: string) => type === PlaybookReportType.ABM

  const getOffsetAndTotal = (type: string) => {
    if (isOutboundType(type))
      return {
        offset: offsets.outbound + 1,
        max: priorityPayload.playbookTypes.outbound.length,
      }
    else if (isABMType(type))
      return {
        offset: offsets.ABM + 1,
        max: priorityPayload.playbookTypes.ABM.length,
      }
    else
      return {
        offset: offsets.nonSpecial + 1,
        max: priorityPayload.playbookTypes.nonSpecial.length,
      }
  }

  if (R.isEmpty(prioritizedPlaybooks) && !isLoading) {
    return (
      <Fallback
        isPriority
        isEmpty={R.isEmpty(prioritizedPlaybooks)}
        isLoading={isLoading}
      />
    )
  }

  return (
    <Table
      height="100%"
      rows={prioritizedPlaybooks.slice(
        tableState.pagination.pageIndex * tableState.pagination.pageSize,
        (tableState.pagination.pageIndex + tableState.pagination.pageSize) *
          tableState.pagination.pageSize
      )}
      columns={[
        {
          header: 'Priority',
          value: 'priority',
          size: 107,
          cell: (playbook) => {
            const handleChange = (option: { label: string; value: number }) =>
              changePriority(option, playbook)

            const priorityList = generateSelectOptions(
              getOffsetAndTotal(playbook.reportType)
            )

            return (
              <Box css={{ '& [class*="container"]': { width: '75px' } }}>
                <Select
                  label=""
                  onChange={(option) =>
                    handleChange(option as { label: string; value: number })
                  }
                  options={priorityList}
                  value={{
                    label: String(playbook.priority),
                    value: playbook.priority,
                  }}
                  menuPosition="fixed"
                  size="fullWidth"
                />
              </Box>
            )
          },
        },
        {
          header: 'Playbook',
          value: 'name',
          size: 449,
          cell: ({ name, id }) => (
            <Flex direction="column" css={{ gap: '4px' }}>
              <Text variant="bodyBold">{name}</Text>
              <Text variant="metadata">{id}</Text>
            </Flex>
          ),
        },
        {
          header: 'Status',
          value: 'status',
          size: 106,
          cell: ({ status }) => {
            return (
              <StatusBadge
                variant={status === 'ENABLED' ? 'success' : 'error'}
                size="medium"
              >
                {status === 'ENABLED' ? 'ON' : 'OFF'}
              </StatusBadge>
            )
          },
        },
        {
          header: 'Type',
          value: 'reportType',
          size: 258,
          cell: ({ updatedAt, reportType }) => {
            const hasCLPTag =
              updatedAt > CLP_AS_PLAYBOOK_RELEASE_TIME &&
              LANDING_PAGE_PLAYBOOK_REPORT_TYPES.includes(
                reportType as unknown as PlaybookLandingPageTypes
              )

            const type = hasCLPTag
              ? CLP_PLAYBOOK_TYPE
              : REPORT_TYPE_TO_TABLE_NAME[reportType]

            return (
              <Flex alignItems="center">
                <Text variant="body">{type}</Text>
                {isOutboundType(reportType) && (
                  <Tooltip content="Outbound playbooks are always highest priority">
                    <Icon icon={faCircleInfo} />
                  </Tooltip>
                )}
                {isABMType(reportType) && (
                  <Tooltip content="ABM playbooks are always prioritized above normal playbooks.">
                    <Icon icon={faCircleInfo} />
                  </Tooltip>
                )}
              </Flex>
            )
          },
        },
        {
          header: 'Last updated',
          value: 'updatedAt',
          size: 200,
          cell: ({ updatedAt }) => (
            <Text variant="body">{formatTime(updatedAt, 'MM/DD/YY')}</Text>
          ),
        },
        {
          header: 'Updated by',
          value: 'updatedAuthorId',
          size: 250,
          cell: ({ updatedAuthorId }) => {
            const user = orgUsers[updatedAuthorId] || {}

            return (
              <Text variant="body" css={{ width: '230px' }} truncate>
                {user.name || user.email || 'teammate'}
              </Text>
            )
          },
        },
      ]}
      isLoading={isLoading}
      pagination={{
        rowTotal: total,
        enableClientSidePagination: true,
        defaultPagination: tableState.pagination,
      }}
    />
  )
}

export default PrioritizeTable
